import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@mui/material';
import { Remove, Add } from '@mui/icons-material';
import { colors } from 'styles/colors';

export interface FAQItem {
  question: string;
  answer: string;
  defaultExpanded?: boolean;
}

interface FAQAccordionProps {
  faqData: FAQItem[];
  defaultExpandedIndex?: number;
  onToggle?: (questionId: string, question: string) => void;
}

const FAQAccordion: React.FC<FAQAccordionProps> = ({
  faqData,
  defaultExpandedIndex = 0,
  onToggle
}) => {
  const [expandedFAQ, setExpandedFAQ] = useState<string | false>(`faq-${defaultExpandedIndex}`);

  const handleFAQChange = (panel: string, question: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedFAQ(isExpanded ? panel : false);
    if (onToggle && isExpanded) {
      onToggle(panel, question);
    }
  };

  return (
    <>
      {faqData.map((faq, index) => (
        <Accordion
          key={index}
          expanded={expandedFAQ === `faq-${index}`}
          onChange={handleFAQChange(`faq-${index}`, faq.question)}
          sx={{
            mb: 2,
            border: `1px solid ${colors.lighterGray2}`,
            borderRadius: { xs: '6px !important', sm: '8px !important' },
            '&:before': {
              display: 'none',
            },
            boxShadow: 'none',
            '&.Mui-expanded': {
              margin: '0 0 16px 0',
            }
          }}
        >
          <AccordionSummary
            expandIcon={
              expandedFAQ === `faq-${index}` ? 
                <Remove sx={{ color: colors.primaryBlue }} /> : 
                <Add sx={{ color: colors.primaryBlue }} />
            }
            sx={{
              backgroundColor: expandedFAQ === `faq-${index}` ? '#F4FAFF' : 'transparent',
              borderRadius: expandedFAQ === `faq-${index}` ? 
                { xs: '6px 6px 0 0', sm: '8px 8px 0 0' } : 
                { xs: '6px', sm: '8px' },
              '& .MuiAccordionSummary-content': {
                margin: '12px 0',
              }
            }}
          >
            <Typography 
              fontWeight={500} 
              sx={{ 
                color: colors.fontDarkBlue,
                fontSize: {xs: '14px', md: '16px'}
              }}
            >
              {faq.question}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ 
            backgroundColor: "#F4FAFF",
            borderRadius: { xs: '0 0 6px 6px', sm: '0 0 8px 8px' }
          }}>
            <Typography
              component="div"
              sx={{
                color: colors.fontLight,
                fontSize: {xs: '12px', md: '16px'},
                lineHeight: 1.6,
              }}
              dangerouslySetInnerHTML={{ __html: faq.answer }}
            />
          </AccordionDetails>
        </Accordion>
      ))}
    </>
  );
};

export default FAQAccordion;